import React, { useEffect, useState } from 'react';
import { Download, RefreshCw } from 'lucide-react';
import { METRICS, PRESETS, istToday } from './fmt';
import { StaffFilters } from './StaffFilters';

const Seg = ({ value, options, onChange, testId }) => (
  <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden" data-testid={testId}>
    {options.map(([v, l]) => (
      <button key={v} type="button" onClick={() => onChange(v)} data-testid={`${testId}-${v}`}
        className={`px-3 py-2 text-sm font-medium transition-colors ${value === v ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>{l}</button>
    ))}
  </div>
);

// Number input that commits on blur / Enter (avoids a reload per keystroke).
const NumField = ({ value, onCommit, min, max, testId, className = 'w-16' }) => {
  const [draft, setDraft] = useState(String(value));
  useEffect(() => setDraft(String(value)), [value]);
  const commit = () => {
    const n = Number(draft);
    if (Number.isFinite(n) && n >= min && n <= max) onCommit(n); else setDraft(String(value));
  };
  return (
    <input type="number" value={draft} min={min} max={max} onChange={(e) => setDraft(e.target.value)} onBlur={commit}
      onKeyDown={(e) => e.key === 'Enter' && commit()} data-testid={testId}
      className={`${className} border border-gray-300 rounded-lg px-2 py-2 text-sm focus:ring-2 focus:ring-indigo-500 outline-none`} />
  );
};

const Label = ({ children }) => <label className="block text-xs font-semibold text-gray-500 mb-1">{children}</label>;
const dateCls = 'border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 outline-none';

export const RankingsControls = ({ f, setF, modules, shifts = [], filterOptions = {}, noBase = '__none__', lockModule, loading, onRefresh, onExport, canExport }) => {
  const set = (patch) => setF((p) => ({ ...p, ...patch }));
  const preset = (p) => { const [from, to] = p.range(istToday()); set({ from, to, preset: p.id }); };
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4 shadow-sm space-y-4" data-testid="rank-controls">
      <div className="flex flex-wrap items-end gap-4">
        <div>
          <Label>Period</Label>
          <div className="flex flex-wrap items-center gap-2">
            <Seg value={f.preset} options={PRESETS.map((p) => [p.id, p.label])} onChange={(id) => preset(PRESETS.find((p) => p.id === id))} testId="rank-preset" />
            <input type="date" value={f.from} max={f.to} onChange={(e) => set({ from: e.target.value, preset: 'custom' })} className={dateCls} data-testid="rank-date-from" />
            <span className="text-xs text-gray-400">to</span>
            <input type="date" value={f.to} min={f.from} max={istToday()} onChange={(e) => set({ to: e.target.value, preset: 'custom' })} className={dateCls} data-testid="rank-date-to" />
          </div>
        </div>
        {!lockModule && (
          <div>
            <Label>Section</Label>
            <select value={f.module} onChange={(e) => set({ module: e.target.value })} className={dateCls} data-testid="rank-module-select">
              <option value="all">All sections</option>
              {modules.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
        )}
        <div>
          <Label>Shift</Label>
          <Seg value={f.shift} options={[['all', 'All shifts'], ...shifts.map((s) => [s.id, s.name])]} onChange={(shift) => set({ shift })} testId="rank-shift" />
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <button onClick={onRefresh} className="inline-flex items-center gap-1.5 bg-gray-800 text-white rounded-lg px-3.5 py-2 text-sm font-medium hover:bg-gray-900" data-testid="rank-refresh-btn">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
          <button onClick={onExport} disabled={!canExport} className="inline-flex items-center gap-1.5 bg-emerald-600 text-white rounded-lg px-3.5 py-2 text-sm font-medium hover:bg-emerald-700 disabled:opacity-40" data-testid="rank-export-csv-btn">
            <Download className="w-4 h-4" /> Export CSV
          </button>
        </div>
      </div>
      <div className="flex flex-wrap items-end gap-4">
        <div>
          <Label>Rank by</Label>
          <Seg value={f.rankBy} options={Object.entries(METRICS).map(([k, m]) => [k, m.short])} onChange={(rankBy) => set({ rankBy })} testId="rank-by" />
        </div>
        <div>
          <Label>Low performer</Label>
          <div className="flex items-center gap-2">
            <Seg value={f.lowRule} options={[['avg', 'Below % of op average'], ['target', 'Below target']]} onChange={(lowRule) => set({ lowRule })} testId="rank-low-rule" />
            {f.lowRule === 'avg' && <><NumField value={f.lowPct} min={10} max={100} onCommit={(lowPct) => set({ lowPct })} testId="rank-low-pct" /><span className="text-xs text-gray-500">%</span></>}
          </div>
        </div>
        <div>
          <Label>Min. days in operation</Label>
          <NumField value={f.minDays} min={0} max={31} onCommit={(minDays) => set({ minDays })} testId="rank-min-days" />
        </div>
      </div>
      <StaffFilters value={f.staff} onChange={(staff) => set({ staff })} options={filterOptions} noBase={noBase} />
    </div>
  );
};

export default RankingsControls;
